/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import JsonView from 'react18-json-view';
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  useToast,
} from '@chakra-ui/react';
import 'react18-json-view/src/style.css';
import { useAppSelector } from '../../../../redux/hooks';
import { resetSelectedManualVerificationSessionData } from '../../../../redux/manualVerification/sessionsDetails';
import {
  setSelectedSessionId,
  updateActiveStep as updateManualVerificationActiveStep,
} from '../../../../redux/manualVerification/stepperDetails';
import { getManualVerificationSelectedSessionId } from '../../../../redux/manualVerification/stepperDetails/selectors';
import { resetManualVerificationTemporaryDetails } from '../../../../redux/manualVerification/temporaryDetails';
import {
  getManualVerificationDynamicBodyValues,
  getManualVerificationDynamicURLValues,
} from '../../../../redux/manualVerification/temporaryDetails/selectors';
import { resetNetworkStore } from '../../../../redux/providerBuilder/networkRequestStore';
import { resetProviderDetails } from '../../../../redux/providerBuilder/providerDetails';
import { getProviderDetails } from '../../../../redux/providerBuilder/providerDetails/selectors';
import { ProviderDetails } from '../../../../redux/providerBuilder/providerDetails/types';
import { resetSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import { resetSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest';
import { createParamValues } from '../../../../utils/manual-verification-session/create-param-values';
import {
  updateManualVerificationSession,
  updateSessionV2,
} from '../../../../utils/manual-verification-session/update-manual-verification-session-utils';
import { createManualVerificationClaimData } from '../../../../utils/manual-verification-session/utils';
import { editProvider } from '../../../../utils/provider-builder/register-provider-utils';
import { getFirebaseIdToken } from '../../../../utils/utils';

const ManualVerificationReviewAndPublishScreen = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const cardRef = useRef<HTMLDivElement>(null);

  const selectedInstances = useAppSelector(getSelectedInstances);
  const dynamicURLValues = useAppSelector(getManualVerificationDynamicURLValues);
  const dynamicBodyValues = useAppSelector(getManualVerificationDynamicBodyValues);
  const sessionId = useAppSelector(getManualVerificationSelectedSessionId);

  const providerDetails = useAppSelector(getProviderDetails);

  const [publishClaim, setPublishClaim] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [claimData, setClaimData] = useState<any>({});

  useEffect(() => {
    createClaimData();
  }, []);

  useEffect(() => {
    return resetTempDetails;
  }, []);

  const resetTempDetails = () => {
    dispatch(resetManualVerificationTemporaryDetails());
  };

  async function createClaimData() {
    const paramValues = createParamValues(
      selectedInstances,
      dynamicURLValues,
      dynamicBodyValues
    );
    const result = await createManualVerificationClaimData(
      providerDetails?.httpProviderId,
      paramValues
    );
    if (result?.isSuccess) {
      setClaimData(result?.data?.claimData ?? {});
    }
  }

  async function publishProvider() {
    try {
      setButtonLoading(true);
      const accessToken = (await getFirebaseIdToken()) ?? '';

      const payload: ProviderDetails = {
        ...providerDetails,
      };
      const message1 = await editProvider({
        providerDetails: payload,
        selectedNetworkRequest: null,
        accessToken,
      });

      if (publishClaim) {
        const paramValues = createParamValues(
          selectedInstances,
          dynamicURLValues,
          dynamicBodyValues
        );
        const message2: any = await updateManualVerificationSession(
          accessToken,
          sessionId,
          'VERIFIED',
          paramValues
        );
        await updateSessionV2(sessionId, 'PROOF_MANUAL_VERIFICATION_SUCCESS');
        // if (selectedSessionData?.status === 'PENDING' || selectedSessionData?.sessionV2Data?.status === 'PROOF_MANUAL_VERIFICATION_SUBMITED') {
        //     console.log('update session v2')
        //     await updateSessionV2(sessionId, 'PROOF_MANUAL_VERIFICATION_SUCCESS')
        // }

        if (!message2.isSuccess) {
          toast({
            title: 'Error',
            description: 'something went wrong',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          return;
        }
      }

      if (!message1.isSuccess) {
        toast({
          title: 'Error',
          description: 'something went wrong',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      } else {
        toast({
          title: 'Successfull',
          description: '',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        dispatch(resetProviderDetails());
        dispatch(resetNetworkStore());
        dispatch(resetSelectedInstances());
        dispatch(resetSelectedNetworkRequest());
        dispatch(resetSelectedManualVerificationSessionData());
        dispatch(resetManualVerificationTemporaryDetails());

        if (params?.sessionId?.length) {
          dispatch(setSelectedSessionId(''));
          dispatch(updateManualVerificationActiveStep(1));
          navigate('/manual-verification');
        } else {
          dispatch(updateManualVerificationActiveStep(2));
        }
      }
    } catch (e) {
      toast({
        title: 'Error',
        description: 'something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setButtonLoading(false);
    }
  }

  const handleGoBack = () => {
    dispatch(updateManualVerificationActiveStep(6));
  };

  return (
    <>
      <Card w="100%" ref={cardRef} h="100%">
        <CardBody p={0}>
          <Flex direction="column" gap={5} p={8}>
            <Heading className="large-font">Review & Publish</Heading>
            <Flex justifyContent="space-between" gap="50px">
              <Flex
                direction="column"
                width="calc(50% - 25px)" /* 50% minus half of the gap */
                flexGrow={0}
                flexShrink={0}
              >
                <Heading className="medium-font">Claim</Heading>
                <Box
                  borderRadius={7}
                  bgColor="gray.50"
                  h="40vh"
                  overflowX="auto" /* Horizontal scroll */
                  overflowY="scroll"
                  p={2}
                >
                  <JsonView
                    src={claimData}
                    enableClipboard={false}
                    collapseStringsAfterLength={30}
                  />
                </Box>
              </Flex>

              <Flex
                direction="column"
                width="calc(50% - 25px)" /* 50% minus half of the gap */
                flexGrow={0}
                flexShrink={0}
              >
                <Heading className="medium-font">Provider</Heading>
                <Box
                  width="full"
                  borderRadius={7}
                  bgColor="gray.50"
                  h="40vh"
                  overflowX="auto" /* Horizontal scroll */
                  overflowY="scroll"
                  p={2}
                >
                  <JsonView
                    src={providerDetails}
                    enableClipboard={false}
                    collapseStringsAfterLength={30}
                  />
                </Box>
              </Flex>
            </Flex>

            <Flex w="40%" margin="auto" gap={3} direction="column" mt={5} maxW="400px">
              <Flex gap={3}>
                <Checkbox
                  isChecked={publishClaim}
                  isDisabled={buttonLoading}
                  size="lg"
                  colorScheme="blackAlpha"
                  iconColor={publishClaim ? 'white' : ''}
                  bgColor={publishClaim ? 'black' : ''}
                  onChange={() => setPublishClaim(!publishClaim)}
                />
                <Heading className="medium-font">Publish Claim</Heading>
              </Flex>

              <Button
                colorScheme="blackAlpha"
                bgColor="black"
                px={120}
                p={6}
                borderRadius={10}
                isLoading={buttonLoading}
                onClick={publishProvider}
                w="100%"
              >
                Publish Provider
              </Button>
            </Flex>
          </Flex>
        </CardBody>
        <Box w="full" backgroundColor="gray.50" px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
                transform: 'scale(1.05)',
              }}
              isDisabled={buttonLoading}
              onClick={handleGoBack}
            >
              Back
            </Button>
          </Flex>
        </Box>
      </Card>
    </>
  );
};

export default ManualVerificationReviewAndPublishScreen;
